import { LinkType as AnalyticsLinkType } from '@easeltv/analytics';
import {
    Link,
    LinkBackground,
    LinkDisplayType,
    LinkType,
    Theme,
} from '@easeltv/client-sdk';
import { ReactNode } from 'react';

import { getSystemPath } from '../../../utilities';
import { buildImageUrl } from '../../../utilities/imageUtilities/imageUtilities';

/* ---------------------------------- Types --------------------------------- */

/** @type `BasePropsT`
 * #### Base properties for the linkAtom
 */
type BasePropsT = {
    background: LinkBackground;
    env: string; // 'dev' or 'prod'
    luminance: Theme['sections']['footer']['luminance'];
    tenant: string; // usually a three letter string, e.g. 'etv'
    darkImageId?: string; // an id for fetching an image
    lightImageId?: string; // an id for fetching an image
};

/** @type `LabelPropsT`
 * #### Type for the LabelProps's props object
 */
export type LabelPropsT = BasePropsT & {
    display: LinkDisplayType; // determines display type, text, image or both
    text: string; // the text that appears to the user eg. 'Login',
};

/** @type `ImagePropsT`
 * #### Type for the ImageProps's props object
 */
export type ImagePropsT = BasePropsT & {
    darkImageId: string; // mandatory to create images
    lightImageId: string; // mandatory to create images
};

/* --------------------------------- Helpers -------------------------------- */

/**
 * getImageSrc
 *
 * Creates a path to an image
 *
 * @param data ImageProps
 * @returns An image url
 */
export const getImageSrc = (data: ImagePropsT): string => {
    const { lightImageId, darkImageId, tenant, env, background, luminance } =
        data;
    const imageLuminance =
        background === LinkBackground.Feature
            ? luminance.feature
            : luminance.background;
    const imageId = imageLuminance === 'dark' ? lightImageId : darkImageId;
    return buildImageUrl(imageId, 'fit', 300, 73, tenant, env).replace(
        '/rendition',
        '',
    );
};

/**
 * getLinkHref
 *
 * Returns the url from a Link object
 * Returns the `#` character if no type is matched
 *
 * @param link Link
 * @returns A string, the link's url
 */
export const getLinkHref = (link: Link): string => {
    switch (link.type) {
        case LinkType.External: {
            return link.url;
        }
        case LinkType.Internal: {
            return `/internal/${link.itemId}`;
        }
        case LinkType.System: {
            return getSystemPath(link.systemPage);
        }
        default:
            return '#';
    }
};

/**
 * createLabel
 *
 * Creates a label for a link: text (in a span), an image or both.
 * Returns an empty React fragment if no type is matched
 *
 * @param data LabelProps
 * @returns A JSX element: a text, image or both
 */
export const createLabel = (data: LabelPropsT): ReactNode => {
    const {
        display,
        background,
        text,
        tenant,
        env,
        luminance,
        lightImageId,
        darkImageId,
    } = data;

    switch (display) {
        case LinkDisplayType.Text: {
            return <span>{text}</span>;
        }
        case LinkDisplayType.Image: {
            if (!lightImageId || !darkImageId) return <span>{text}</span>;

            const imageSrc = getImageSrc({
                lightImageId,
                darkImageId,
                background,
                tenant,
                env,
                luminance,
            });
            return <img src={imageSrc} loading="lazy" alt={text} />;
        }
        case LinkDisplayType.TextAndImage: {
            if (!lightImageId || !darkImageId) return <span>{text}</span>;

            const imageSrc = getImageSrc({
                lightImageId,
                darkImageId,
                background,
                tenant,
                env,
                luminance,
            });
            return (
                <>
                    <img src={imageSrc} loading="lazy" alt={text} />
                    <span>{text}</span>
                </>
            );
        }
        default: {
            return null;
        }
    }
};

/**
 * getItemType
 *
 * Returns the itemType as an Analytics type for convenience
 *
 * @param itemId An id usually in the form 'page_0000001'
 * @returns The itemType eg. page
 */
const getItemType = (itemId: string) =>
    itemId.split('_')[0] as 'product' | 'collection' | 'page' | 'contributor';

/**
 * getDestination
 *
 * Creates a destination object used for Analytics from and SDK link object
 *
 * @param link Link
 * @returns A Link object of Analytics LinkType
 */
export const getDestination = (link: Link): AnalyticsLinkType => {
    switch (link.type) {
        case LinkType.External: {
            return {
                type: link.type,
                destinationUrl: link.url,
            };
        }
        case LinkType.Internal: {
            return {
                type: link.type,
                itemId: link.itemId,
                itemType: getItemType(link.itemId),
            };
        }
        case LinkType.System:
        default: {
            return {
                type: link.type,
                pageId: link.systemPage,
            };
        }
    }
};
